import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { confirmPasswordReset, verifyPasswordResetCode, sendPasswordResetEmail } from 'firebase/auth';
import { auth } from '../firebase';
import {
  Container,
  Paper,
  TextField,
  Button,
  Typography,
  Box,
  Alert,
  CircularProgress,
  InputAdornment,
  IconButton
} from '@mui/material';
import { Visibility, VisibilityOff, LockReset } from '@mui/icons-material';
import { toast } from 'react-toastify';

const PasswordResetPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const oobCode = searchParams.get('oobCode');
  const mode = searchParams.get('mode');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [email, setEmail] = useState('');
  const [isVerified, setIsVerified] = useState(false);
  const [passwordError, setPasswordError] = useState('');
  const [confirmPasswordError, setConfirmPasswordError] = useState('');
  const [resendLoading, setResendLoading] = useState(false);
  const [showResend, setShowResend] = useState(false);
  const [resendEmail, setResendEmail] = useState('');
  
  useEffect(() => {
  if (!oobCode || mode !== 'resetPassword') {
    setError('Invalid or expired password reset link. Please request a new password reset.');
    setShowResend(true); // Show the resend form immediately
  }
}, [oobCode, mode]);

  useEffect(() => {
    const checkCodeAndGetEmail = async () => {
      if (oobCode && mode === 'resetPassword') {
        try {
          const userEmail = await verifyPasswordResetCode(auth, oobCode);
          setEmail(userEmail);
          setIsVerified(true);
        } catch (error) {
  setError("This link is invalid or has expired. Please request a new password reset.");
  setIsVerified(false);
  setShowResend(true);
  setResendEmail(''); // ask user for email to resend
}
      }
    };
    checkCodeAndGetEmail();
  }, [oobCode, mode]);

  const validatePassword = (pwd) => {
    if (pwd.length < 12) {
      return 'Password must be at least 12 characters long with one number, one upper case and one lower case letter';
    }
    return '';
  };

  const handlePasswordChange = (e) => {
    const newPassword = e.target.value;
    setPassword(newPassword);
    const error = validatePassword(newPassword);
    setPasswordError(error);
    if (confirmPassword && newPassword !== confirmPassword) {
      setConfirmPasswordError('Passwords do not match');
    } else if (confirmPassword) {
      setConfirmPasswordError('');
    }
  };

  const handleConfirmPasswordChange = (e) => {
    const newConfirmPassword = e.target.value;
    setConfirmPassword(newConfirmPassword);
    if (password !== newConfirmPassword) {
      setConfirmPasswordError('Passwords do not match');
    } else {
      setConfirmPasswordError('');
    }
  };

  const isFormValid = () => {
    return (
      password &&
      confirmPassword &&
      password === confirmPassword &&
      !passwordError &&
      !confirmPasswordError &&
      password.length >= 6
    );
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!isVerified) return;
    if (!isFormValid()) {
      setError('Please fix the errors above before submitting.');
      return;
    }
    setLoading(true);
    setError('');
    try {
      await confirmPasswordReset(auth, oobCode, password);
      toast.success('Password reset successful! Please sign in with your new password.');
      navigate('/login', {
        state: {
          message: 'Password reset successful. Please sign in with your new password.'
        }
      });
    } catch (error) {
  console.error('Password reset error:', error);
  let errorMessage = 'Failed to reset password. ';
  if (error.code === 'auth/expired-action-code') {
    errorMessage += 'The password reset link has expired. We just sent you a fresh link.';
    try {
      await sendPasswordResetEmail(auth, email);
      toast.info('A new password reset link has been emailed to you.');
      setShowResend(false);
    } catch (e2) {
      errorMessage = 'This link expired. Enter your email below to get a new link.';
      setShowResend(true);
    }
  } else if (error.code === 'auth/invalid-action-code') {
    errorMessage += 'The password reset link is invalid. Enter your email below to get a new link.';
    setShowResend(true);
  } else if (error.code === 'auth/weak-password') {
    errorMessage += 'The password is too weak. Please choose a stronger password.';
  } else {
    errorMessage += error.message || 'Please try again.';
  }
  setError(errorMessage);
  setLoading(false);
}
  };

  const handleResend = async () => {
  const targetEmail = email || resendEmail;
  if (!targetEmail) {
    setShowResend(true);
    setError('Enter your email to get a fresh reset link.');
    return;
  }
  try {
    setResendLoading(true);
    await sendPasswordResetEmail(auth, targetEmail);
    setShowResend(false);
    toast.info('A new password reset link has been emailed to you.');
  } catch (e) {
    setShowResend(true);
    setError(e.message || 'Failed to send a new link. Please try again.');
  } finally {
    setResendLoading(false);
  }
};

  return (
    <Container maxWidth="sm" sx={{ mt: 8, mb: 4 }}>
      <Paper elevation={3} sx={{ p: 4 }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mb: 3 }}>
          <LockReset sx={{ fontSize: 48, color: 'primary.main', mb: 2 }} />
          <Typography variant="h4" component="h1" gutterBottom align="center">
            Reset Your Password
          </Typography>
          <Typography variant="body1" color="text.secondary" align="center">
            Enter your new password below
          </Typography>
        </Box>
        {error && (
  <Alert severity="error" sx={{ mb: 3 }}>
    {error}
    {showResend && (
      <Box sx={{ mt: 1, display: 'flex', gap: 1, alignItems: 'center' }}>
        <TextField
          size="small"
          label="Email"
          type="email"
          value={resendEmail}
          onChange={(e) => setResendEmail(e.target.value)}
          disabled={resendLoading}
        />
        <Button
          size="small"
          onClick={handleResend}
          disabled={resendLoading || !resendEmail}
          sx={{ textTransform: 'none' }}
        >
          {resendLoading ? 'Sending…' : 'Send New Reset Link'}
        </Button>
      </Box>
    )}
  </Alert>
)}
        {oobCode && mode === 'resetPassword' && isVerified && (
        <Box component="form" onSubmit={handleSubmit} sx={{ mt: 2 }}>
            <TextField
              fullWidth
              type={showPassword ? 'text' : 'password'}
              label="New Password"
              value={password}
              onChange={handlePasswordChange}
              error={!!passwordError}
              helperText={passwordError || 'Password must be at least 12 characters long with one number, one upper case and one lower case letter'}
              margin="normal"
              required
              disabled={loading}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowPassword(!showPassword)}
                      edge="end"
                      disabled={loading}
                    >
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
            <TextField
              fullWidth
              type={showConfirmPassword ? 'text' : 'password'}
              label="Confirm New Password"
              value={confirmPassword}
              onChange={handleConfirmPasswordChange}
              error={!!confirmPasswordError}
              helperText={confirmPasswordError || 'Re-enter your new password'}
              margin="normal"
              required
              disabled={loading}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                      edge="end"
                      disabled={loading}
                    >
                      {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
            <Button
              type="submit"
              fullWidth
              variant="contained"
              disabled={!isFormValid() || loading || !isVerified}
              sx={{ mt: 3, mb: 2, py: 1.5 }}
            >
              {loading ? (
                <>
                  <CircularProgress size={20} sx={{ mr: 1 }} />
                  Resetting Password...
                </>
              ) : (
                'Reset Password'
              )}
            </Button>
            <Box sx={{ textAlign: 'center', mt: 2 }}>
              <Button
                onClick={() => navigate('/login')}
                disabled={loading}
                sx={{ textTransform: 'none' }}
              >
                Back to Login
              </Button>
            </Box>
          </Box>
        )}
      </Paper>
    </Container>
  );
};
export default PasswordResetPage;